import { useState, useMemo } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useParams } from 'react-router-dom';
import { SharedProposalHeader } from './SharedProposalHeader';
import { ItineraryData, IItineraryData } from '@/lib/builder/models/itinerary.model';
import { ItineraryBuilder } from '@/lib/builder/models/itinerary-builder.model';
import { ActivityFactory } from '@/lib/builder/models/activity.model';
import DayBuilderStep from '@/lib/builder/DayBuilderStep';
import ItinerarySidebar from '@/lib/builder/ItinerarySidebar';
import { AccommodationActivityClass } from '@/lib/builder/models/activity.model';
import { createCheckInOutActivities } from '@/components/utils/createCheckOutFromCheckIn';
import { TravelRequestModal } from '@/lib/requests/TravelRequestModal';
import { useSharedPayment } from '../hooks/useSharedPayment';

interface SharedProposalViewProps {
  itinerary: any;
  sharedBy: {
    first_name: string;
    last_name: string;
    logo_url?: string;
    logo_small_url?: string;
    agency_name?: string;
  };
  paymentEnabled?: boolean;
  password?: string;
}

export const SharedProposalView = ({ itinerary, sharedBy, paymentEnabled = false, password }: SharedProposalViewProps) => {
  const { t } = useI18n();
  const { shareToken } = useParams<{ shareToken: string }>();
  const { createPaymentLink, isCreatingPayment, paymentError } = useSharedPayment(shareToken!);
  const [showPaymentError, setShowPaymentError] = useState(false);
  
  console.log('[SharedProposalView] Props:', {
    paymentEnabled,
    totalPrice: itinerary.total,
    hasItinerary: !!itinerary,
    hasSharedBy: !!sharedBy
  });
  
  // Inizializza ItineraryData dai dati ricevuti
  const itineraryData = useMemo(() => {
    const data = new ItineraryData(itinerary as IItineraryData);
    
    // Se ci sono attività, le trasformiamo con ActivityFactory
    if (itinerary.itinerary_days && itinerary.itinerary_days.length > 0) {
      const allActivities = itinerary.itinerary_days.flatMap((day: any) => 
        day.activities.map((act: any) => ActivityFactory.create(act))
      );
      
      // Crea check-in/out per accommodation
      const modifiedActivities = allActivities.flatMap((activity: any) => {
        if (activity instanceof AccommodationActivityClass) {
          const [checkIn, checkOut] = createCheckInOutActivities(activity);
          return [checkIn, checkOut];
        }
        return activity;
      });
      
      data.setItineraryDays(modifiedActivities);
    }
    
    return data;
  }, [itinerary]);

  // Inizializza builder
  const builder = useMemo(() => {
    const itineraryBuilder = new ItineraryBuilder();
    
    if (itineraryData.start_date && itineraryData.end_date) {
      if (itineraryData.itinerary_days && itineraryData.itinerary_days.length > 0) {
        const daysData = itineraryData.itinerary_days.map(day => ({
          date: day.date,
          activities: day.activities.map(activity => ActivityFactory.create(activity))
        }));
        itineraryBuilder.loadFromArray(daysData, itineraryData.start_date, itineraryData.end_date);
      } else {
        itineraryBuilder.initializeDays(itineraryData.start_date, itineraryData.end_date);
      }
    }
    
    return itineraryBuilder;
  }, [itineraryData]);

  // Stato per il giorno selezionato
  const [selectedDate, setSelectedDate] = useState<string>(itineraryData.firstDay || itineraryData.start_date);

  // Stato per la modale Travel Request
  const [isTravelRequestModalOpen, setIsTravelRequestModalOpen] = useState(false);
  const travelRequest = itinerary.travel_request;

  const handleDaySelect = (date: string) => {
    setSelectedDate(date);
  };

  const handleViewTravelRequest = () => {
    console.log('[SharedProposalView] Opening travel request modal, travelRequest:', travelRequest);
    setIsTravelRequestModalOpen(true);
  };

  const handleCloseTravelRequestModal = () => {
    setIsTravelRequestModalOpen(false);
  };

  const handlePayNow = async () => {
    setShowPaymentError(false);
    
    const customerEmail = itinerary?.travel_request?.contact_email;
    const result = await createPaymentLink(password, customerEmail);
    
    if (result.success && result.url) {
      // Redirect to Stripe payment page
      window.location.href = result.url;
    } else {
      setShowPaymentError(true);
    }
  };

  return (
    <>
      <div className="h-screen flex flex-col bg-gray-50">
        <SharedProposalHeader
          sharedBy={sharedBy}
          totalPrice={itinerary.total}
          cancellationPolicyNotes={itinerary.cancellation_policy_notes}
          paymentEnabled={paymentEnabled}
          onPayNow={handlePayNow}
          isProcessing={isCreatingPayment}
          showError={showPaymentError}
          errorMessage={paymentError || undefined}
        />
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Sidebar */}
          <div className="w-full lg:w-80 order-first lg:order-none overflow-y-auto">
            <ItinerarySidebar
              data={itineraryData}
              builder={builder}
              onEditTrip={travelRequest ? handleViewTravelRequest : undefined}
              onDaySelect={handleDaySelect}
              selectedDate={selectedDate}
              isReadOnly={true}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 flex justify-center overflow-y-auto order-last lg:order-none bg-white">
            <div className="w-full h-full">
              <DayBuilderStep
                data={itineraryData}
                onDataChange={() => {}} // Nessuna modifica permessa
                canProceed={false}
                setCanProceed={() => {}}
                selectedDate={selectedDate}
                isReadOnly={true}
                isTraveler={true}
                onBuilderChange={() => {}}
                failedActivityIds={new Set<string>()}
                onActivityReorder={() => {}}
                onNext={() => {}}
                onPrevious={() => {}}
                onSave={() => {}}
                onCancel={() => {}}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Travel Request Modal */}
      <TravelRequestModal
        isOpen={isTravelRequestModalOpen && !!travelRequest}
        onClose={handleCloseTravelRequestModal}
        travelRequest={travelRequest || {} as any}
        showActions={false}
        isAdmin={false}
      />
    </>
  );
};
